import { AppContainer } from "../../components";
import { useStats } from "../../../hooks/useStats";
import { useEffect, useState } from "react";
import {
  IoCheckmarkCircle,
  IoChevronDown,
  IoCloseCircle,
  IoHelpCircleOutline,
  IoTimeOutline,
} from "react-icons/io5";

const formatPercent = (value) =>
  typeof value === "number" ? `${value.toFixed(1)}%` : "-";

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  return new Date(timestamp * 1000).toLocaleString("es-MX", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const getDateKey = (timestamp) => {
  if (!timestamp) return "sin-fecha";
  const date = new Date(timestamp * 1000);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateLabel = (dateKey) => {
  if (dateKey === "sin-fecha") return "Sin fecha";
  const [year, month, day] = dateKey.split("-");
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
  ).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getFixtureOutcome = (fixture) => {
  switch (fixture.reason) {
    case "HIT":
      return "hit";
    case "NO_HT_GOAL":
      return "failed";
    case "GOAL_BEFORE_MIN_16":
      return "beforeMin16";
    case "MISSING_GOAL_MINUTE":
      return "missingGoalMinute";
    default:
      // null is only expected on pending fixtures
      return fixture.over05HtHit ? "hit" : "failed";
  }
};

const OUTCOME_SORT_ORDER = {
  hit: 0,
  failed: 1,
  beforeMin16: 2,
  missingGoalMinute: 3,
};

const getFixtureSortOrder = (fixture) =>
  OUTCOME_SORT_ORDER[getFixtureOutcome(fixture)] ?? 2;

const summarizeFixtures = (fixtures = []) => {
  let hits = 0;
  let failed = 0;
  let beforeMin16 = 0;
  let missingGoalMinute = 0;

  fixtures.forEach((fixture) => {
    const outcome = getFixtureOutcome(fixture);
    if (outcome === "hit") hits += 1;
    else if (outcome === "failed") failed += 1;
    else if (outcome === "beforeMin16") beforeMin16 += 1;
    else if (outcome === "missingGoalMinute") missingGoalMinute += 1;
  });

  return {
    total: fixtures.length,
    hits,
    failed,
    beforeMin16,
    missingGoalMinute,
  };
};

const groupFinishedFixturesByDate = (fixtures = []) => {
  const groups = fixtures.reduce((acc, fixture) => {
    const key = getDateKey(fixture.timestamp);
    if (!acc[key]) acc[key] = [];
    acc[key].push(fixture);
    return acc;
  }, {});

  return Object.entries(groups)
    .sort(([dateA], [dateB]) => {
      if (dateA === "sin-fecha") return 1;
      if (dateB === "sin-fecha") return -1;
      return dateB.localeCompare(dateA);
    })
    .map(([dateKey, dateFixtures]) => ({
      dateKey,
      label: formatDateLabel(dateKey),
      fixtures: [...dateFixtures].sort(
        (a, b) => getFixtureSortOrder(a) - getFixtureSortOrder(b),
      ),
      summary: summarizeFixtures(dateFixtures),
    }));
};

const FixtureOdds = ({ odds }) => {
  const [open, setOpen] = useState(false);
  const oddsList = odds ?? [];

  if (!oddsList.length) return null;

  return (
    <section className='mt-2 w-full'>
      <button
        type='button'
        className='group flex w-full items-center justify-between py-1 text-left'
        onClick={() => setOpen((prev) => !prev)}>
        <span className='text-[10px] font-medium text-white/70'>
          Odds
          <span className='ml-1.5 text-white/40 text-[10px]'>
            ({oddsList.length})
          </span>
        </span>
        <IoChevronDown
          className={`h-3.5 w-3.5 text-white/50 transition-transform group-hover:text-white ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className='mt-1 flex flex-col border-t border-white/10 pt-1.5'>
          {oddsList.map((odd, index) => (
            <span key={index} className='mb-2 text-[10px] text-white/50'>
              {odd.name} - {odd.odd.name} {"HT ----- "}
              {odd.odd.value}
            </span>
          ))}
        </div>
      )}
    </section>
  );
};

const OutcomeIcon = ({ outcome }) => {
  if (outcome === "hit") {
    return <IoCheckmarkCircle className='h-4 w-4 shrink-0 text-emerald-400' />;
  }
  if (outcome === "beforeMin16") {
    return <IoTimeOutline className='h-4 w-4 shrink-0 text-gray-400' />;
  }
  if (outcome === "missingGoalMinute") {
    return <IoHelpCircleOutline className='h-4 w-4 shrink-0 text-amber-400' />;
  }
  return <IoCloseCircle className='h-4 w-4 shrink-0 text-rose-400' />;
};

const FinishedFixtureCard = ({ fixture }) => {
  const outcome = getFixtureOutcome(fixture);

  return (
    <article className='py-2.5 text-[11px] leading-tight'>
      <div className='grid grid-cols-[1fr_auto] items-start gap-3'>
        <div className='min-w-0'>
          <p className='truncate font-medium text-white/95 text-[10px]'>
            {fixture.homeTeam}{" "}
            <span className='mx-1 text-white/35 text-[10px]'>vs</span>
            {fixture.awayTeam}
          </p>
          <p className='mt-1 truncate text-[9px] uppercase tracking-wide text-white/40 text-[10px]'>
            {fixture.league}
          </p>
        </div>

        <OutcomeIcon outcome={outcome} />
      </div>

      <div className='mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-white/50'>
        <span className='text-white/70 text-[10px]'>
          HT {fixture.goalsHT?.home ?? 0}-{fixture.goalsHT?.away ?? 0}
        </span>
        {fixture.firstGoalMinute != null && (
          <span className='text-[10px]'>1st goal {fixture.firstGoalMinute}'</span>
        )}
        {outcome === "missingGoalMinute" && (
          <span className='text-[10px] text-amber-400'>Not goal data</span>
        )}
      </div>
    </article>
  );
};

const DateFixturesAccordion = ({ group, open, onToggle }) => (
  <section className='w-full border-b border-white/10'>
    <button
      type='button'
      className='group w-full py-2.5 text-left'
      onClick={onToggle}>
      <div className='flex items-center justify-between gap-3'>
        <span className='text-[12px] font-semibold text-white/90'>
          {group.label}
        </span>
        <IoChevronDown
          className={`h-4 w-4 shrink-0 text-white/50 transition-transform group-hover:text-white ${
            open ? "rotate-180" : ""
          }`}
        />
      </div>
      <div className='mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-white/50'>
        <span className='text-[10px]'>
          Total:{" "}
          <b className='font-medium text-white/80'>{group.summary.total}</b>
        </span>
        <span className='text-emerald-400 text-[10px]'>
          Hits: <b className='font-medium'>{group.summary.hits}</b>
        </span>
        <span className='text-rose-400 text-[10px]'>
          Failed: <b className='font-medium'>{group.summary.failed}</b>
        </span>
        <span className='text-[10px]'>
          Before min 16:{" "}
          <b className='font-medium text-white/80'>
            {group.summary.beforeMin16}
          </b>
        </span>
        <span className='text-amber-400 text-[10px]'>
          Not goal data:{" "}
          <b className='font-medium'>{group.summary.missingGoalMinute}</b>
        </span>
      </div>
    </button>

    {open && (
      <div className='divide-y divide-white/10 border-t border-white/10'>
        {group.fixtures.map((fixture) => (
          <FinishedFixtureCard key={fixture.fixtureId} fixture={fixture} />
        ))}
      </div>
    )}
  </section>
);

const FinishedFixturesSection = ({ title, fixtures = [], open, onToggle }) => {
  const [openDateKey, setOpenDateKey] = useState(null);
  const groups = groupFinishedFixturesByDate(fixtures);

  return (
    <section className='w-full'>
      <button
        type='button'
        className='group w-full flex items-center justify-between py-2.5 text-left'
        onClick={onToggle}>
        <span className='flex items-center gap-2 text-[14px] font-semibold text-white/80'>
          {title}
          <span className='rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] tracking-normal text-white/55'>
            {fixtures.length}
          </span>
        </span>
        <IoChevronDown
          className={`h-4 w-4 text-white/50 transition-transform group-hover:text-white ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className='max-h-[65dvh] overflow-y-auto border-t border-white/10'>
          {groups.length ? (
            groups.map((group) => (
              <DateFixturesAccordion
                key={group.dateKey}
                group={group}
                open={openDateKey === group.dateKey}
                onToggle={() =>
                  setOpenDateKey(
                    openDateKey === group.dateKey ? null : group.dateKey,
                  )
                }
              />
            ))
          ) : (
            <p className='py-3 text-[10px] text-white/40'>
              No hay partidos para mostrar.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

const FixtureSection = ({
  title,
  fixtures = [],
  open,
  onToggle,
  showProbabilities = true,
}) => (
  <section className='w-full'>
    <button
      type='button'
      className='group w-full flex items-center justify-between py-2.5 text-left'
      onClick={onToggle}>
      <span className='flex items-center gap-2 text-[14px] font-semibold text-white/80'>
        {title}
        <span className='rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] tracking-normal text-white/55'>
          {fixtures.length}
        </span>
      </span>
      <IoChevronDown
        className={`h-4 w-4 text-white/50 transition-transform group-hover:text-white ${open ? "rotate-180" : ""}`}
      />
    </button>

    {open && (
      <div className='max-h-[65dvh] overflow-y-auto border-t border-white/10 divide-y divide-white/10'>
        {fixtures.length ? (
          fixtures.map((fixture) => (
            <article
              key={fixture.fixtureId}
              className='py-2.5 text-[11px] leading-tight'>
              <div className='grid grid-cols-[1fr_auto] items-start gap-3'>
                <div className='min-w-0'>
                  <p className='truncate font-medium text-white/95 text-[10px]'>
                    {fixture.homeTeam}{" "}
                    <span className='mx-1 text-white/35 text-[10px]'>vs</span>
                    {fixture.awayTeam}
                  </p>
                  <div>
                    <p className='mt-1 truncate text-[9px] uppercase tracking-wide text-white/40 text-[10px]'>
                      {fixture.league}
                    </p>
                    {fixture.leagueRate && (
                      <div className='my-1'>
                        <span className='text-[10px]'>
                          League rate: {fixture.leagueRate.place}
                        </span>
                        <span className='text-[10px] ml-2'>
                          total fixtures:{" "}
                          {fixture.leagueRate.hitFixtures +
                            fixture.leagueRate.failedFixtures}
                        </span>
                        <span className='text-[10px] text-emerald-400 ml-2'>
                          hit: {fixture.leagueRate.hitRate}%{" "}
                          {` (${fixture.leagueRate.hitFixtures})`}
                        </span>
                        <span className='text-[10px] text-rose-400 ml-2'>
                          failed: {fixture.leagueRate.failedRate}%
                          {` (${fixture.leagueRate.failedFixtures})`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className='mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-white/50'>
                {fixture.timestamp && (
                  <span className='text-[10px]'>
                    {formatDate(fixture.timestamp)}
                  </span>
                )}

                {showProbabilities && (
                  <section className='flex mt-2'>
                    <span className='text-white/70 text-[10px]'>
                      Strict Venue over 0.5 HT{" "}
                      {formatPercent(fixture.probabilities?.strictVenueOver05)}
                    </span>
                    <span className='text-white/70 text-[10px] ml-4'>
                      Overall over 0.5 HT{" "}
                      {formatPercent(fixture.probabilities?.overallOver05)}
                    </span>
                  </section>
                )}

                <FixtureOdds odds={fixture.odds} />
              </div>
            </article>
          ))
        ) : (
          <p className='py-3 text-[10px] text-white/40'>
            No hay partidos para mostrar.
          </p>
        )}
      </div>
    )}
  </section>
);

const LeagueStatsSection = ({ leagues = [] }) => (
  <section className='w-full'>
    <div className='max-h-[60dvh] overflow-y-auto divide-y divide-white/10'>
      {leagues.length ? (
        leagues.map((league) => (
          <article key={league.leagueId} className='py-3 text-[10px]'>
            <div className='flex items-center justify-between gap-3'>
              <p className='truncate font-medium text-white/95 text-[12px]'>
                {league.league}
              </p>
              <span className='shrink-0 text-emerald-400 text-[12px]'>
                {formatPercent(league.hitRate)} hit rate
              </span>
            </div>
            <div className='my-3 grid grid-cols-4 gap-2 text-white/50'>
              <span className='text-[10px]'>
                <b className='font-medium text-white/80'>
                  {league.finished ?? 0}
                </b>{" "}
                finished
              </span>
              <span className='text-[10px] text-emerald-400'>
                <b className='font-medium'>{league.hits?.matches ?? 0}</b> hits
              </span>
              <span className='text-[10px] text-rose-400'>
                <b className='font-medium'>{league.failed?.matches ?? 0}</b>{" "}
                failed
              </span>
              <span className='text-[10px]'>
                <b className='font-medium text-white/80'>
                  {league.scoreBeforeMin16?.matches ?? 0}
                </b>{" "}
                before min 16'
              </span>
            </div>
          </article>
        ))
      ) : (
        <p className='py-3 text-[10px] text-white/40'>
          No league stats available.
        </p>
      )}
    </div>
  </section>
);

const Over05HTStatsView = ({ title, fetchStats, showProbabilities = true }) => {
  const [stats, setStats] = useState(null);
  const [activeSection, setActiveSection] = useState("summary");
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    const loadStats = async () => {
      const data = await fetchStats();
      if (data) setStats(data);
    };
    loadStats();
    // The hook creates a new function on each render; stats load only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <AppContainer>
        <div className='flex h-full flex-col items-center w-full z-20 px-5'>
          <p className='text-sm text-white font-semibold text-center my-4'>
            {title}
          </p>

          {stats && (
            <div className='w-full max-w-[480px] flex flex-1 flex-col overflow-hidden'>
              <nav
                className='grid grid-cols-3 shrink-0'
                aria-label='Over 0.5 HT sections'>
                {[
                  ["summary", "Summary"],
                  ["fixtures", "Fixtures Details"],
                  ["leagues", "Leagues Details"],
                ].map(([section, label]) => (
                  <button
                    key={section}
                    type='button'
                    onClick={() => setActiveSection(section)}
                    className={`relative px-1 py-3 text-[10px] font-semibold transition-colors ${
                      activeSection === section ? "text-white" : "text-white/25"
                    }`}>
                    {label}
                    {activeSection === section && (
                      <span className='absolute inset-x-2 bottom-0 h-px bg-white' />
                    )}
                  </button>
                ))}
              </nav>

              <div className='flex-1 overflow-y-auto py-5'>
                {activeSection === "summary" && (
                  <section className='text-[11px] text-white'>
                    <div className='grid grid-cols-3 mt-5 border-y border-white/10 divide-x divide-white/10 text-center'>
                      <div className='py-2'>
                        <p className='text-sm font-semibold'>
                          {stats.totalMatches}
                        </p>
                        <p className='mt-0.5 text-[9px] uppercase tracking-wide text-white/80'>
                          Total
                        </p>
                      </div>
                      <div className='py-2'>
                        <p className='text-sm font-semibold'>
                          {stats.finishedMatches}
                        </p>
                        <p className='mt-0.5 text-[9px] uppercase tracking-wide text-white/80'>
                          Finished
                        </p>
                      </div>
                      <div className='py-2'>
                        <p className='text-sm font-semibold'>
                          {stats.pendingMatches}
                        </p>
                        <p className='mt-0.5 text-[9px] uppercase tracking-wide text-white/80'>
                          Pending
                        </p>
                      </div>
                    </div>
                    <div className='mt-6 space-y-2 text-[10px] text-white/60'>
                      <p className='flex justify-between'>
                        <span className='text-[14px]'>Hits</span>
                        <span className='text-[14px] text-emerald-400'>
                          {stats.hits?.matches ?? 0} (
                          {formatPercent(stats.hits?.percent)})
                        </span>
                      </p>
                      <p className='flex justify-between'>
                        <span className='text-[14px]'>Failed</span>
                        <span className='text-[14px] font-medium text-rose-400'>
                          {stats.failed.matches ?? 0} (
                          {formatPercent(stats.failed.percent)})
                        </span>
                      </p>

                      <p className='flex justify-between'>
                        <span className='text-[14px]'>Score before min 16</span>
                        <span className='text-[14px]'>
                          {stats.scoreBeforeMin16.matches ?? 0} (
                          {formatPercent(stats.scoreBeforeMin16.percent)})
                        </span>
                      </p>
                      <p className='flex justify-between'>
                        <span className='text-[14px]'>Not goal data</span>
                        <span className='text-[14px]'>
                          {stats.missingGoalMinute.matches ?? 0} (
                          {formatPercent(stats.missingGoalMinute.percent)})
                        </span>
                      </p>
                    </div>
                  </section>
                )}

                {activeSection === "fixtures" && (
                  <section>
                    <FinishedFixturesSection
                      title='Ending Fixtures'
                      fixtures={stats.finishedFixtures}
                      open={openSection === "finished"}
                      onToggle={() =>
                        setOpenSection(
                          openSection === "finished" ? null : "finished",
                        )
                      }
                    />
                    <FixtureSection
                      title='Pending Fixtures'
                      fixtures={stats.pendingFixtures}
                      open={openSection === "pending"}
                      onToggle={() =>
                        setOpenSection(
                          openSection === "pending" ? null : "pending",
                        )
                      }
                      showProbabilities={showProbabilities}
                    />
                  </section>
                )}

                {activeSection === "leagues" && (
                  <LeagueStatsSection leagues={stats.leaguesStats} />
                )}
              </div>
            </div>
          )}
        </div>
      </AppContainer>
    </>
  );
};

export const Over05HTStats = () => {
  const { getOver05HTStats } = useStats();
  return (
    <Over05HTStatsView
      title='Stats - Over 0.5 HT'
      fetchStats={getOver05HTStats}
      showProbabilities
    />
  );
};

export const Over05HTOddsStats = () => {
  const { getOver05HTOddsStats } = useStats();
  return (
    <Over05HTStatsView
      title='Stats - Over 0.5 HT Odds'
      fetchStats={getOver05HTOddsStats}
      showProbabilities={false}
    />
  );
};
